import React, { useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import {
  Loader2, Settings as SettingsIcon, Clock, MapPin, Tag, Target, Mail, Save,
  KeyRound, Zap, Check, Database, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

function TimeField({ value, onChange }) {
  return (
    <input
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="settings-time-input"
      className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#E52431]/60 outline-none transition w-full"
    />
  );
}

function TagListField({ value, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (v) => onChange(value.filter((x) => x !== v));
  return (
    <div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
        />
        <button
          type="button"
          onClick={add}
          data-testid={`${testId}-add`}
          className="px-4 rounded-xl bg-white/10 hover:bg-white/15 text-sm text-white/80 transition"
        >
          Ajouter
        </button>
      </div>
      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 text-white/80"
              data-testid={`${testId}-tag`}
            >
              {tag}
              <button type="button" onClick={() => remove(tag)} className="text-white/50 hover:text-white" aria-label="Retirer">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SectorPicker({ available, value, onChange }) {
  const toggle = (key) => {
    onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="sector-picker">
      {available.map((s) => {
        const active = value.includes(s.key);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => toggle(s.key)}
            data-testid={`sector-${s.key}`}
            className={`group text-left rounded-xl border px-4 py-3 transition ${
              active
                ? "border-[#E52431]/40 bg-[#E52431]/10 text-white"
                : "border-white/10 bg-white/[0.02] text-white/75 hover:bg-white/[0.05]"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{s.label}</span>
              <span className={`w-5 h-5 rounded-md border flex items-center justify-center text-[10px] ${
                active ? "border-[#E52431] bg-[#E52431] text-white" : "border-white/20"
              }`}>
                {active && <Check size={11} />}
              </span>
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.1em] text-white/40 font-mono">
              NAF {s.naf_codes.join(" · ")}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Row({ Icon, label, hint, children }) {
  return (
    <div className="py-5 border-b border-white/5 last:border-b-0 grid md:grid-cols-[260px_1fr] gap-3 md:gap-8 items-start">
      <div>
        <div className="flex items-center gap-2 text-sm font-medium text-white">
          <Icon size={14} className="text-[#E52431]" /> {label}
        </div>
        {hint && <div className="text-xs text-white/45 mt-1 leading-relaxed">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function PapperToken({ isSet, preview, value, onChange }) {
  const [editing, setEditing] = useState(!isSet);
  if (isSet && !editing) {
    return (
      <div className="flex items-center gap-3" data-testid="pappers-token-set">
        <div className="px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm font-mono inline-flex items-center gap-2">
          <Check size={14} /> Clé configurée <span className="text-emerald-300/70">({preview})</span>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs uppercase tracking-[0.1em] text-white/55 hover:text-white"
          data-testid="pappers-token-replace"
        >
          Remplacer
        </button>
      </div>
    );
  }
  return (
    <input
      type="password"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={isSet ? "Coller une nouvelle clé…" : "Coller votre clé API Pappers…"}
      data-testid="pappers-token-input"
      className="w-full max-w-md bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none font-mono text-sm"
    />
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { timeZone: "Indian/Reunion", dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function RunsHistory({ runs }) {
  if (!runs || runs.length === 0) {
    return <div className="text-xs text-white/40">Aucun run pour le moment.</div>;
  }
  return (
    <div className="space-y-2" data-testid="pappers-runs">
      {runs.map((r, i) => {
        const res = r.result || {};
        const ok = res.status === "ok";
        const skipped = res.status === "skipped";
        return (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : skipped ? "bg-amber-400" : "bg-[#E52431]"}`} />
                <span className="text-white/80 text-xs">{r.type === "pappers_manual" ? "Manuel" : "Planifié"}</span>
                <span className="text-white/40 text-xs">{formatDateTime(r.ran_at)}</span>
              </div>
              <div className="mt-1 text-xs text-white/55 truncate">
                {ok && `${res.inserted || 0} insérés · ${res.enriched || 0} enrichis · ${res.qualified || 0} qualifiés · ${res.auto_rejected || 0} auto-rejetés`}
                {skipped && `Ignoré : ${res.reason}`}
                {!ok && !skipped && (res.error || res.status || "Erreur")}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [sectorsList, setSectorsList] = useState([]);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [newToken, setNewToken] = useState("");

  const loadRuns = async () => {
    try {
      const { data } = await adminApi.get("/admin/pappers/runs?limit=5");
      setRuns(data.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, sec] = await Promise.all([
          adminApi.get("/admin/settings"),
          adminApi.get("/admin/pappers/sectors"),
        ]);
        if (!cancelled) {
          setData(s.data);
          setSectorsList(sec.data.sectors || []);
        }
        await loadRuns();
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await adminApi.put("/admin/settings", {
        daily_run_time: data.daily_run_time,
        sectors: data.sectors,
        zones: data.zones,
        qualification_threshold: data.qualification_threshold,
        social_threshold: data.social_threshold ?? 30,
        max_prospects_per_day: data.max_prospects_per_day,
        notification_email: data.notification_email,
        pappers_api_token: newToken, // empty string preserves existing on backend
        pappers_enabled: data.pappers_enabled !== false,
      });
      toast.success("Réglages enregistrés.");
      setNewToken("");
      // Reload to get fresh masked token + next_run
      const { data: refreshed } = await adminApi.get("/admin/settings");
      setData(refreshed);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(typeof detail === "string" ? detail : "Erreur inconnue");
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const onRunNow = async () => {
    if (!data.pappers_api_token_set && !newToken) {
      toast.error("Configurez d'abord la clé API Pappers (puis Enregistrer).");
      return;
    }
    if (!window.confirm("Lancer la routine Pappers maintenant ?\n(Fetch + scoring auto, génération IA manuelle)")) return;
    setRunning(true);
    try {
      const { data: result } = await adminApi.post("/admin/pappers/run-now");
      if (result.status === "ok") {
        toast.success(`${result.inserted || 0} insérés · ${result.enriched || 0} enrichis · ${result.qualified || 0} qualifiés · ${result.auto_rejected || 0} auto-rejetés`);
      } else if (result.status === "skipped") {
        toast.info(`Run ignoré : ${result.reason}`);
      } else {
        toast.error(`Erreur : ${result.error || "inconnue"}`);
      }
      await loadRuns();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Run échoué.");
    } finally {
      setRunning(false);
    }
  };

  if (!data) {
    return (
      <div className="px-6 md:px-10 py-10 flex items-center gap-2 text-white/50 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  // Migrate legacy free-text sectors → keys if needed (defensive)
  const sectorValues = Array.isArray(data.sectors) ? data.sectors : [];
  const sectorKeysOnly = sectorValues.filter((s) => sectorsList.some((x) => x.key === s));

  return (
    <div className="px-6 md:px-10 py-10 max-w-5xl" data-testid="admin-settings">
      <div className="text-xs tracking-[2px] text-white/40 font-semibold">— RÉGLAGES</div>
      <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight inline-flex items-center gap-3">
        <SettingsIcon size={26} className="text-white/40" /> Paramètres de la routine
      </h1>
      <p className="mt-2 text-white/55 text-sm">
        Configurez la routine quotidienne automatique : fetch depuis Pappers, scoring auto, auto-rejet sous le seuil.
      </p>

      {/* ---- Pappers / Intégration ---- */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-2">
        <div className="py-5 border-b border-white/5">
          <div className="text-[10px] tracking-[0.12em] uppercase text-white/40 font-semibold mb-2 inline-flex items-center gap-2">
            <Database size={11} /> Intégration Pappers
          </div>
          <div className="text-sm text-white/55">
            Pappers.fr fournit la base d&apos;entreprises françaises (SIREN/SIRET). La clé API est requise pour activer le fetch quotidien.
          </div>
        </div>
        <Row Icon={KeyRound} label="Clé API Pappers" hint="Obtenue sur pappers.fr/api après création de compte. Stockée chiffrée côté serveur, jamais renvoyée en clair.">
          <PapperToken
            isSet={!!data.pappers_api_token_set}
            preview={data.pappers_api_token_preview}
            value={newToken}
            onChange={setNewToken}
          />
        </Row>
        <Row Icon={CalendarClock} label="Prochain run planifié" hint="L'horaire est en Indian/Reunion. Modifiez ci-dessous pour changer.">
          <div className="text-sm text-white/85 font-mono" data-testid="pappers-next-run">
            {data.pappers_next_run ? formatDateTime(data.pappers_next_run) : "—"}
          </div>
        </Row>
        <Row Icon={Zap} label="Déclenchement manuel" hint="Lance immédiatement un fetch + score (sans attendre l'heure planifiée). Utile pour tester.">
          <button
            type="button"
            onClick={onRunNow}
            disabled={running}
            data-testid="pappers-run-now"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-60"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap size={14} />}
            {running ? "En cours…" : "Lancer maintenant"}
          </button>
        </Row>
        <Row Icon={Clock} label="Historique des runs" hint="5 derniers déclenchements (auto ou manuels).">
          <RunsHistory runs={runs} />
        </Row>
      </div>

      {/* ---- Configuration routine ---- */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-2">
        <Row Icon={Clock} label="Heure d'exécution" hint="Heure quotidienne (24h, Indian/Reunion).">
          <div className="max-w-[160px]">
            <TimeField value={data.daily_run_time} onChange={(v) => setData({ ...data, daily_run_time: v })} />
          </div>
        </Row>
        <Row Icon={Tag} label="Secteurs à scanner" hint="Catégories ciblées sur Pappers (code NAF affiché en gris). Cliquez pour activer.">
          {sectorsList.length > 0 ? (
            <SectorPicker
              available={sectorsList}
              value={sectorKeysOnly}
              onChange={(v) => setData({ ...data, sectors: v })}
            />
          ) : (
            <div className="text-xs text-white/40">Chargement…</div>
          )}
        </Row>
        <Row Icon={MapPin} label="Zones (département)" hint="Code département à 2-3 chiffres. Ex : 974 (La Réunion), 75 (Paris).">
          <TagListField
            value={data.zones}
            onChange={(v) => setData({ ...data, zones: v })}
            placeholder="Ex : 974"
            testId="settings-zone"
          />
        </Row>
        <Row Icon={Target} label="Seuil de qualification" hint="Sous ce score, les prospects sont AUTO-REJETÉS (filtre qualité).">
          <div className="flex items-center gap-4 max-w-md">
            <input
              type="range" min="0" max="100"
              value={data.qualification_threshold}
              onChange={(e) => setData({ ...data, qualification_threshold: parseInt(e.target.value, 10) })}
              data-testid="settings-threshold-range"
              className="flex-1 accent-[#E52431]"
            />
            <input
              type="number" min="0" max="100"
              value={data.qualification_threshold}
              onChange={(e) => setData({ ...data, qualification_threshold: parseInt(e.target.value, 10) || 0 })}
              data-testid="settings-threshold-input"
              className="w-20 text-center bg-white/5 border border-white/10 rounded-xl py-2 text-white focus:border-[#E52431]/60 outline-none"
            />
            <span className="text-sm text-white/50">/100</span>
          </div>
        </Row>
        <Row Icon={Target} label="Nb max de prospects / jour" hint="Limite quotidienne pour rester gérable manuellement.">
          <div className="max-w-[140px]">
            <input
              type="number" min="1" max="200"
              value={data.max_prospects_per_day}
              onChange={(e) => setData({ ...data, max_prospects_per_day: parseInt(e.target.value, 10) || 1 })}
              data-testid="settings-max-per-day-input"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#E52431]/60 outline-none"
            />
          </div>
        </Row>
        <Row Icon={Mail} label="Email de notification" hint="Pour les récaps quotidiens (futur).">
          <input
            type="email"
            value={data.notification_email || ""}
            onChange={(e) => setData({ ...data, notification_email: e.target.value })}
            data-testid="settings-notif-email-input"
            placeholder="admin@exemple.com"
            className="w-full max-w-md bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
          />
        </Row>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-xl border border-[#E52431]/30 bg-[#E52431]/10 text-sm text-[#FFB4BB]" data-testid="admin-settings-error">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="admin-settings-save"
          className="inline-flex items-center gap-2 rounded-full gradient-bg text-white font-semibold px-6 py-3 text-xs uppercase tracking-[0.15em] hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={14} />}
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        {data.updated_at && (
          <span className="text-xs text-white/40">
            Dernière mise à jour : {new Date(data.updated_at).toLocaleString("fr-FR", { timeZone: "Indian/Reunion" })}
          </span>
        )}
      </div>
    </div>
  );
}
